import React from 'react';
import { Leaf, Droplets, Zap, TreePine, Recycle, Calendar, Target } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMissionImpacts } from '../hooks/useMissionImpacts';
import LoadingSpinner from './LoadingSpinner';

export default function UserImpactHistory() {
  const { user } = useAuth();
  const { userImpactHistory, impactSummary, loading, fetchUserImpactHistory } = useMissionImpacts();
  
  React.useEffect(() => {
    if (user) {
      fetchUserImpactHistory(user.id);
    }
  }, [user]);
  
  if (loading || !user) {
    return <LoadingSpinner />;
  }
  
  if (userImpactHistory.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Impact History Yet</h3>
        <p className="text-gray-600">
          Complete missions to start tracking your environmental impact!
        </p>
      </div>
    );
  }
  
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Get icon for impact type
  const getImpactIcon = (impactType: string) => {
    switch (impactType) {
      case 'co2_saved':
        return <Leaf className="h-5 w-5 text-green-600" />;
      case 'waste_reduced':
        return <Recycle className="h-5 w-5 text-orange-600" />;
      case 'trees_planted':
        return <TreePine className="h-5 w-5 text-emerald-600" />;
      case 'water_saved':
        return <Droplets className="h-5 w-5 text-blue-600" />;
      case 'energy_saved':
        return <Zap className="h-5 w-5 text-yellow-600" />;
      default:
        return <Target className="h-5 w-5 text-gray-600" />;
    }
  };
  
  // Get color for impact type
  const getImpactColor = (impactType: string) => {
    switch (impactType) {
      case 'co2_saved':
        return 'bg-green-100 text-green-800';
      case 'waste_reduced':
        return 'bg-orange-100 text-orange-800';
      case 'trees_planted':
        return 'bg-emerald-100 text-emerald-800';
      case 'water_saved':
        return 'bg-blue-100 text-blue-800';
      case 'energy_saved':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get label for impact type
  const getImpactLabel = (impactType: string) => {
    switch (impactType) {
      case 'co2_saved':
        return 'CO₂ Saved';
      case 'waste_reduced':
        return 'Waste Reduced';
      case 'trees_planted':
        return 'Trees Planted';
      case 'water_saved':
        return 'Water Saved';
      case 'energy_saved':
        return 'Energy Saved';
      default:
        return impactType.replace('_', ' ');
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Impact Summary */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Your Environmental Impact</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {impactSummary.co2_saved > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Leaf className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">CO₂ Saved</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{impactSummary.co2_saved.toFixed(1)} kg</p>
            </div>
          )}
          
          {impactSummary.waste_reduced > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Recycle className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-gray-900">Waste Reduced</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{impactSummary.waste_reduced.toFixed(1)} kg</p>
            </div>
          )}
          
          {impactSummary.trees_planted > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <TreePine className="h-5 w-5 text-emerald-600" />
                <span className="font-medium text-gray-900">Trees Planted</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{impactSummary.trees_planted}</p>
            </div>
          )}
          
          {impactSummary.water_saved > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Droplets className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Water Saved</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{impactSummary.water_saved.toFixed(0)} L</p>
            </div>
          )}
          
          {impactSummary.energy_saved > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Zap className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-gray-900">Energy Saved</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{impactSummary.energy_saved.toFixed(1)} kWh</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 pt-4 border-t border-emerald-200 text-center">
          <p className="text-emerald-700">
            You've made <strong>{impactSummary.total_actions}</strong> environmental contributions across <strong>{impactSummary.missions_completed}</strong> missions!
          </p>
        </div>
      </div>
      
      {/* Impact History Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Impact History</h3>
        
        <div className="space-y-6">
          {userImpactHistory.map((impact, index) => (
            <div key={impact.id} className="relative pl-8 pb-6">
              {/* Timeline connector */}
              {index < userImpactHistory.length - 1 && (
                <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"></div>
              )}
              
              {/* Timeline dot */}
              <div className="absolute left-0 top-0 w-8 h-8 flex items-center justify-center">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getImpactColor(impact.impact_type_name)}`}>
                  {getImpactIcon(impact.impact_type_name)}
                </div>
              </div>
              
              {/* Content */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {getImpactLabel(impact.impact_type_name)}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getImpactColor(impact.impact_type_name)}`}>
                      +{impact.quantity} {impact.unit}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(impact.created_at)}</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-2">
                  From mission: <span className="font-medium">{impact.missions?.title || 'Unknown Mission'}</span>
                </p>
                
                <div className="text-xs text-gray-500">
                  Category: {impact.missions?.category || 'General'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}